import { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";

const nameSchema = z.string().trim().min(1, "Display name is required").max(80, "Max 80 characters");
const emailSchema = z.string().trim().email("Invalid email").max(255);
const passwordSchema = z.string().min(8, "Min 8 characters").max(72, "Max 72 characters");

const Section = ({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) => (
  <div className="rounded-2xl bg-muted/40 border border-border p-5 space-y-3">
    <div>
      <h3 className="font-display text-lg">{title}</h3>
      {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    </div>
    {children}
  </div>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const AccountSettings = () => {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [initialName, setInitialName] = useState("");
  const [email, setEmail] = useState(user?.email ?? "");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle().then(({ data }) => {
      const n = data?.display_name ?? "";
      setDisplayName(n);
      setInitialName(n);
    });
    setEmail(user.email ?? "");
  }, [user]);

  if (!user) return null;

  const saveName = async () => {
    const parsed = nameSchema.safeParse(displayName);
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);
    setSavingName(true);
    const { error } = await supabase.from("profiles").update({ display_name: parsed.data }).eq("id", user.id);
    if (error) {
      setSavingName(false);
      return toast.error(error.message);
    }
    // If this user owns an athlete profile, keep the public athlete name in sync.
    const { data: owned } = await supabase
      .from("athletes")
      .select("id")
      .eq("owner_user_id", user.id)
      .maybeSingle();
    if (owned?.id) {
      const { error: aErr } = await supabase.from("athletes").update({ name: parsed.data }).eq("id", owned.id);
      if (aErr) {
        setSavingName(false);
        return toast.error(`Profile name updated, but athlete page sync failed: ${aErr.message}`);
      }
    }
    setSavingName(false);
    setInitialName(parsed.data);
    window.dispatchEvent(new CustomEvent("profile:updated", { detail: { display_name: parsed.data } }));
    toast.success("Display name updated across your profile.");
  };

  const saveEmail = async () => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);
    if (parsed.data === user.email) return toast.info("That's already your email");
    setSavingEmail(true);
    const { error } = await supabase.auth.updateUser({ email: parsed.data });
    setSavingEmail(false);
    if (error) return toast.error(error.message);
    toast.success("Confirmation sent — check both your old and new inbox to complete the change.");
  };

  const savePassword = async () => {
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) return toast.error(parsed.error.issues[0].message);
    if (password !== passwordConfirm) return toast.error("Passwords don't match");
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: parsed.data });
    setSavingPassword(false);
    if (error) return toast.error(error.message);
    setPassword("");
    setPasswordConfirm("");
    toast.success("Password updated");
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <Section title="Display name" description="Shown across the site where your account appears.">
        <Field label="Display name">
          <input className={inp} value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={80} />
        </Field>
        <div className="flex justify-end">
          <button
            onClick={saveName}
            disabled={savingName || displayName === initialName}
            className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
          >
            {savingName ? "Saving…" : "Save name"}
          </button>
        </div>
      </Section>

      <Section title="Email" description="We'll send a confirmation link to your current and new address to complete the change.">
        <Field label="Email">
          <input type="email" className={inp} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
        </Field>
        <div className="flex justify-end">
          <button
            onClick={saveEmail}
            disabled={savingEmail || email === user.email}
            className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
          >
            {savingEmail ? "Sending…" : "Update email"}
          </button>
        </div>
      </Section>

      <Section title="Password" description="Set or change your password. Adds a password credential to Google-only accounts so you can also sign in with email.">
        <Field label="New password">
          <input type="password" className={inp} value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} maxLength={72} />
        </Field>
        <Field label="Confirm password">
          <input type="password" className={inp} value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} minLength={8} maxLength={72} />
        </Field>
        <div className="flex justify-end">
          <button
            onClick={savePassword}
            disabled={savingPassword || !password}
            className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
          >
            {savingPassword ? "Saving…" : "Update password"}
          </button>
        </div>
      </Section>
    </div>
  );
};

export default AccountSettings;
